import { z } from 'zod';
import { BAR_PAYMENT_METHODS, PAYMENT_METHODS, PAYMENT_RECORD_PROVIDERS, PAYMENT_STATUSES, REFUND_STATUSES } from '@bulk/types';
import { cursorSchema, dateOnlySchema, dateTimeSchema, idSchema, keysetPageSchema, paginationQuerySchema } from './common';
import { barTakingsSchema } from './bar';
import { paymentSchema, refundSchema } from './payments';
import { cashShiftSchema } from './reception';

export const financeRangeQuerySchema = z.object({
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
});
export type FinanceRangeQuery = z.infer<typeof financeRangeQuerySchema>;

export const financeBreakdownSchema = z.object({
  key: z.string(),
  label: z.string(),
  amountMinor: z.number().int(),
  count: z.number().int(),
});
export type FinanceBreakdownDto = z.infer<typeof financeBreakdownSchema>;

export const financeSummarySchema = z.object({
  from: z.string(),
  to: z.string(),
  currency: z.string(),
  /** The branches counted, for someone who works only at some; null for the whole gym. */
  branches: z.array(z.string()).nullable(),
  grossMinor: z.number().int(),
  refundedMinor: z.number().int(),
  netMinor: z.number().int(),
  paymentCount: z.number().int(),
  refundCount: z.number().int(),
  pendingCount: z.number().int(),
  byMethod: z.array(financeBreakdownSchema),
  byProvider: z.array(financeBreakdownSchema),
  byPlan: z.array(financeBreakdownSchema),
  byStaff: z.array(financeBreakdownSchema),
  daily: z.array(
    z.object({
      date: z.string(),
      grossMinor: z.number().int(),
      refundedMinor: z.number().int(),
      count: z.number().int(),
    }),
  ),
  newMembers: z.number().int(),
  activeMemberships: z.number().int(),
  expiringIn7Days: z.number().int(),
});
export type FinanceSummaryDto = z.infer<typeof financeSummarySchema>;

export const ledgerQuerySchema = paginationQuerySchema.extend({
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
  status: z.enum(PAYMENT_STATUSES).optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
  provider: z.enum(PAYMENT_RECORD_PROVIDERS).optional(),
  memberId: idSchema.optional(),
  q: z.string().trim().max(120).optional(),
  cursor: cursorSchema.optional(),
});
export type LedgerQuery = z.infer<typeof ledgerQuerySchema>;

export const ledgerRowSchema = paymentSchema.extend({
  memberNumber: z.string().nullable(),
  planName: z.string().nullable(),
  recordedByName: z.string().nullable(),
});
export type LedgerRowDto = z.infer<typeof ledgerRowSchema>;

export const ledgerPageSchema = keysetPageSchema(ledgerRowSchema).extend({
  /** The branches counted, for someone who works only at some; null for the whole gym. */
  branches: z.array(z.string()).nullable(),
});
export type LedgerPageDto = z.infer<typeof ledgerPageSchema>;

export const REFUND_SOURCES = ['payments', 'bar'] as const;
export type RefundSource = (typeof REFUND_SOURCES)[number];

export const refundListQuerySchema = paginationQuerySchema.extend({
  source: z.enum(REFUND_SOURCES).optional(),
  status: z.enum(REFUND_STATUSES).optional(),
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
});
export type RefundListQuery = z.infer<typeof refundListQuerySchema>;

export const backofficePaymentRefundSchema = refundSchema.extend({
  source: z.literal('PAYMENT'),
  memberId: idSchema,
  memberName: z.string(),
  memberNumber: z.string(),
  description: z.string().nullable(),
  method: z.enum(PAYMENT_METHODS),
  provider: z.enum(PAYMENT_RECORD_PROVIDERS),
  currency: z.string(),
  paymentAmountMinor: z.number().int(),
  paymentStatus: z.enum(PAYMENT_STATUSES),
  requestedByName: z.string().nullable(),
  reviewedByName: z.string().nullable(),
});
export type BackofficePaymentRefundDto = z.infer<typeof backofficePaymentRefundSchema>;

/** A bar refund is done when it is recorded: no request, no review, always COMPLETED. */
export const backofficeBarRefundSchema = z.object({
  source: z.literal('BAR'),
  id: idSchema,
  saleId: idSchema,
  receiptNumber: z.string(),
  status: z.literal('COMPLETED'),
  amountMinor: z.number().int(),
  saleTotalMinor: z.number().int(),
  currency: z.string(),
  method: z.enum(BAR_PAYMENT_METHODS),
  reason: z.string(),
  memberId: idSchema.nullable(),
  memberName: z.string().nullable(),
  memberNumber: z.string().nullable(),
  lines: z.array(z.object({ name: z.record(z.string(), z.string()), quantity: z.number().int(), amountMinor: z.number().int() })),
  refundedByName: z.string().nullable(),
  locationId: idSchema,
  locationName: z.string(),
  createdAt: z.string(),
});
export type BackofficeBarRefundDto = z.infer<typeof backofficeBarRefundSchema>;

export const backofficeRefundSchema = z.discriminatedUnion('source', [backofficePaymentRefundSchema, backofficeBarRefundSchema]);
export type BackofficeRefundDto = z.infer<typeof backofficeRefundSchema>;

export const dailyReportQuerySchema = z.object({
  date: dateOnlySchema.optional(),
});

export const dailyReportRefundSchema = z.object({
  id: idSchema,
  paymentId: idSchema,
  invoiceNumber: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  memberName: z.string().nullable(),
  description: z.string().nullable(),
  amountMinor: z.number().int(),
  status: z.enum(REFUND_STATUSES),
  reason: z.string().nullable(),
  createdAt: z.string(),
  completedAt: z.string(),
});

export const dailyReportSchema = z.object({
  date: z.string(),
  timezone: z.string(),
  currency: z.string(),
  branchName: z.string().nullable().describe('The branch the report is for (X-Location-Id, or the branches the reader works at); null for the whole gym, the only view with online payments'),
  from: z.string(),
  to: z.string(),
  totals: z.object({
    grossMinor: z.number().int(),
    refundedMinor: z.number().int(),
    netMinor: z.number().int(),
    count: z.number().int(),
  }),
  byMethod: z.array(financeBreakdownSchema),
  byStaff: z.array(financeBreakdownSchema),
  payments: z.array(ledgerRowSchema),
  refunds: z.array(dailyReportRefundSchema),
  shifts: z.array(cashShiftSchema),
  topUps: z.object({
    totalMinor: z.number().int(),
    count: z.number().int(),
    byMethod: z.array(z.object({ method: z.enum(PAYMENT_METHODS), amountMinor: z.number().int(), count: z.number().int() })),
  }).describe("Money taken for members' prepaid balances; held for them, not revenue"),
  bar: z
    .object({ salesMinor: z.number().int(), refundsMinor: z.number().int(), netMinor: z.number().int(), byMethod: z.array(barTakingsSchema) })
    .describe('What the bar sold and refunded that day, by what paid for it; a balance sale spends money already taken as a top-up'),
});
export type DailyReportDto = z.infer<typeof dailyReportSchema>;

export const statementQuerySchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Expected YYYY-MM')
    .optional(),
});
export type StatementQuery = z.infer<typeof statementQuerySchema>;

export const monthlyStatementSchema = z.object({
  month: z.string(),
  from: z.string(),
  to: z.string(),
  timezone: z.string(),
  currency: z.string(),
  generatedAt: z.string(),
  /** The branches counted, for someone who works only at some; null for the whole gym. */
  branches: z.array(z.string()).nullable(),
  gym: z.object({
    name: z.string(),
    slug: z.string(),
    legalName: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
  }),
  totals: z.object({
    grossMinor: z.number().int(),
    refundedMinor: z.number().int(),
    netMinor: z.number().int(),
    paymentCount: z.number().int(),
    refundCount: z.number().int(),
  }),
  byMethod: z.array(financeBreakdownSchema),
  payments: z.array(ledgerRowSchema),
  refunds: z.array(dailyReportRefundSchema),
});
export type MonthlyStatementDto = z.infer<typeof monthlyStatementSchema>;

export const ANALYTICS_GRANULARITIES = ['day', 'week', 'month'] as const;
export type AnalyticsGranularity = (typeof ANALYTICS_GRANULARITIES)[number];

export const ANALYTICS_COMPARISONS = ['previous_period', 'previous_year', 'none'] as const;
export type AnalyticsComparison = (typeof ANALYTICS_COMPARISONS)[number];

export const financeAnalyticsQuerySchema = z.object({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  granularity: z.enum(ANALYTICS_GRANULARITIES).default('day'),
  compare: z.enum(ANALYTICS_COMPARISONS).default('previous_period'),
});
export type FinanceAnalyticsQuery = z.infer<typeof financeAnalyticsQuerySchema>;

export const analyticsMetricSchema = z.object({
  value: z.number().nullable(),
  previous: z.number().nullable(),
});
export type AnalyticsMetricDto = z.infer<typeof analyticsMetricSchema>;

export const analyticsBucketSchema = z.object({
  key: z.string(),
  from: z.string(),
  to: z.string(),
  grossMinor: z.number().int(),
  refundedMinor: z.number().int(),
  netMinor: z.number().int(),
  paymentCount: z.number().int(),
  averagePaymentMinor: z.number().int().nullable(),
  activeMemberships: z.number().int(),
  firstMemberships: z.number().int(),
  repeatMemberships: z.number().int(),
  renewals: z.number().int(),
  automaticRenewals: z.number().int(),
  endedMemberships: z.number().int(),
  churnedMemberships: z.number().int(),
  churnRate: z.number().nullable(),
  renewalRate: z.number().nullable(),
  mrrMinor: z.number().int(),
  newMembers: z.number().int(),
  visits: z.number().int(),
});
export type AnalyticsBucketDto = z.infer<typeof analyticsBucketSchema>;

const analyticsPeriodSchema = z.object({ from: z.string(), to: z.string() });

/** How much of a period came from the daily totals: every day through `through`, computed as of `asOf`; later days were read live. */
export const rollupUseSchema = z.object({ through: z.string(), asOf: z.string() });
export type RollupUseDto = z.infer<typeof rollupUseSchema>;

export const financeAnalyticsSchema = z.object({
  currency: z.string(),
  timezone: z.string(),
  granularity: z.enum(ANALYTICS_GRANULARITIES),
  compare: z.enum(ANALYTICS_COMPARISONS),
  generatedAt: z.string(),
  /** The branches counted, for someone who works only at some; null for the whole gym. */
  branches: z.array(z.string()).nullable(),
  period: analyticsPeriodSchema,
  comparison: analyticsPeriodSchema.nullable(),
  rollup: rollupUseSchema.nullable(),
  revenue: z.object({
    grossMinor: analyticsMetricSchema,
    refundedMinor: analyticsMetricSchema,
    netMinor: analyticsMetricSchema,
    paymentCount: analyticsMetricSchema,
    averagePaymentMinor: analyticsMetricSchema,
    byPlan: z.array(financeBreakdownSchema),
    byMethod: z.array(financeBreakdownSchema),
    byProvider: z.array(financeBreakdownSchema),
    byStaff: z.array(financeBreakdownSchema),
  }),
  recurring: z.object({
    activeMemberships: analyticsMetricSchema,
    firstMemberships: analyticsMetricSchema,
    repeatMemberships: analyticsMetricSchema,
    renewals: analyticsMetricSchema,
    automaticRenewals: analyticsMetricSchema,
    endedMemberships: analyticsMetricSchema,
    churnedMemberships: analyticsMetricSchema,
    churnRate: analyticsMetricSchema,
    renewalRate: analyticsMetricSchema,
    autoRenewShare: analyticsMetricSchema,
    mrrMinor: analyticsMetricSchema,
  }),
  members: z.object({
    newMembers: analyticsMetricSchema,
    activeMembers: analyticsMetricSchema,
    revenuePerActiveMemberMinor: analyticsMetricSchema,
    visits: analyticsMetricSchema,
    visitsPerActiveMember: analyticsMetricSchema,
    atRiskMembers: analyticsMetricSchema,
  }),
  buckets: z.array(analyticsBucketSchema),
  comparisonBuckets: z.array(analyticsBucketSchema),
  outlook: z.object({
    asOf: z.string(),
    endingSoon: z.object({
      days: z.number().int(),
      count: z.number().int(),
      valueMinor: z.number().int(),
      autoRenewCount: z.number().int(),
      autoRenewValueMinor: z.number().int(),
    }),
    pendingPayments: z.object({ days: z.number().int(), count: z.number().int(), amountMinor: z.number().int() }),
    refundsAwaitingApproval: z.object({ count: z.number().int(), amountMinor: z.number().int() }),
  }),
});
export type FinanceAnalyticsDto = z.infer<typeof financeAnalyticsSchema>;
